'use client';
import styles from '@/app/components/shared/table/assetStyles.module.css';
import { RawModel } from '@/types/rawModel';
import { RowSelectionState, Table, Updater } from '@tanstack/react-table';
import { useAssetsTable } from '@/app/assets/components/table/hooks/useAssetsTable';
import { memo, Ref, useEffect, useMemo } from 'react';
import { ExpandableTable } from '@/app/components/shared/table/ExpandableTable';
import { OptimisticAssetsAction } from '@/app/assets/components/table/hooks/useOptimisticAssets';
import { ScrollArea } from '@mantine/core';
import { useAssetTableNavigation } from '@/app/assets/components/table/hooks/useAssetTableNavigation';
import { PendingOutput } from '@/app/assets/actions/rawModelPendingOutputs';
import { AssetInfos } from '@/app/assets/components/table/molecules/AssetInfos';
import { AssetProgress } from './atoms/AssetProgress';
import { expandAllChildren } from '@/utils/tableRow';
import { useSelectedAssetContext } from '@/app/assets/contexts/SelectedAssetContext';

type AssetsTableProps = {
  assets: RawModel[];
  onRowSelectionChange: (updater: Updater<RowSelectionState>) => void;
  rowSelection: RowSelectionState;
  onAssetDelete: (action: OptimisticAssetsAction) => void;
  mRef?: Ref<HTMLDivElement>;
  pendingOutputs: PendingOutput[];
  useOnlyOutputs: boolean;
  addOnJobEndCallback: (key: string, callback: (job: PendingOutput) => void) => void;
  isLoading: boolean;
  isFetching: boolean;
};

function AssetsTable({
  assets,
  onRowSelectionChange,
  rowSelection,
  addOnJobEndCallback,
  onAssetDelete,
  mRef,
  pendingOutputs,
  useOnlyOutputs,
  isLoading,
  isFetching,
}: AssetsTableProps) {
  const readyAssets = useMemo(() => assets.filter((a) => a.upload_status === 'complete'), [assets]);
  const notReadyAssets = useMemo(() => assets.filter((a) => a.upload_status !== 'complete'), [assets]);

  const { table, columnsSpan } = useAssetsTable(
    readyAssets,
    onRowSelectionChange,
    rowSelection,
    false,
    onAssetDelete,
    pendingOutputs,
    useOnlyOutputs
  );

  const { onKeyDown, focusedRow, setFocusedRow } = useAssetTableNavigation(table);

  const { selectedAsset } = useSelectedAssetContext();

  const { table: notReadyTable } = useAssetsTable(
    notReadyAssets,
    onRowSelectionChange,
    rowSelection,
    true,
    onAssetDelete
  );

  useEffect(() => {
    addOnJobEndCallback('expandRow', (job: PendingOutput) => {
      const rowModel = table.getRowModel();
      const row = rowModel.rows.find((row) => row.original.basicInfo.id === job.rawModelId);
      if (row && !row.getIsExpanded()) {
        expandAllChildren(row);
      }
    });
  }, [table, addOnJobEndCallback]);

  useEffect(() => {
    addOnJobEndCallback('deselectRow', (job: PendingOutput) => {
      const isLast = !pendingOutputs
        .filter((job) => job.optimisationStatus !== 'failed')
        .find((pending) => pending.rawModelId === job.rawModelId);
      if (isLast) {
        const rowModel = table.getRowModel();
        const row = rowModel.rows.find((row) => row.original.basicInfo.id === job.rawModelId);
        row?.toggleSelected(false);
      }
    });
  }, [table, addOnJobEndCallback, pendingOutputs]);

  useEffect(() => {
    if (!selectedAsset || selectedAsset.basicInfo.rowType !== 'raw') {
      return;
    }
    const rowModel = table.getRowModel();
    const row = rowModel.rows.find((row) => row.original.basicInfo.id === selectedAsset.basicInfo.id);
    if (row && !row.getIsExpanded()) {
      expandAllChildren(row);
    }
  }, [selectedAsset, table]);

  return (
    <ScrollArea scrollbars='x' type='auto' py='sm' px='0'>
      <ExpandableTable
        mRef={mRef}
        table={table}
        nestedTable={!useOnlyOutputs ? notReadyTable : undefined}
        columnsSpan={columnsSpan}
        rootGridProps={{
          gutter: 10,
          onKeyDown,
          miw: 1210,
        }}
        {...{ focusedRow, setFocusedRow }}
        renderSubComponent={({ row }) => (
          <AssetInfos
            ml={100}
            mb='md'
            errors={row.original.errors}
            warnings={row.original.warnings}
            assetDescription={{
              id: row.original.basicInfo.id,
              name: row.original.basicInfo.name,
              type: row.original.basicInfo.rowType === 'raw' ? 'raw' : 'rapid',
            }}
          />
        )}
        rowDetailsComponent={AssetProgress}
        styleClasses={{
          table: styles.assetsTable,
          tableRow: styles.assetsTableRow,
          tableHeaderRow: styles.assetsTableHeaderRow,
          tableRowExpanded: styles.assetsTableRowExpanded,
          tableRowExpandedInner: styles.assetsTableRowExpandedInner,
          selectedRow: styles.assetsTableSelectedRow,
          selectedExpandedRow: styles.assetsTableSelectedExpandedRow,
          hoverRow: styles.assetHoverRow,
        }}
        getRowId={(row) => `asset-${row.original.basicInfo.id}`}
        isLoading={isLoading}
        isFetching={isFetching}
      />
    </ScrollArea>
  );
}

export default memo(AssetsTable);
